/**
 * useOnboardingMission (TASK-537) — one query for a client's onboarding
 * mission: the 12-step aggregate derived server-side from canonical sources
 * (TASK-536), replacing the checklist's five client-side queries.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../lib/httpClient';

export type OnboardingStepStatus = 'done' | 'attention' | 'waiting' | 'open';

export interface OnboardingStep {
  seq: number;
  key: string;
  label: string;
  actor: 'rm' | 'agent' | 'client' | 'co' | 'bank';
  trust_class: 'auto' | 'review' | 'hard_gate' | 'external';
  status: OnboardingStepStatus;
  detail: string;
  waiting_on: string | null;
  action: string | null;
  /** What an opening action opens. Null when the step has nothing to open yet. */
  document_id?: string | null;
}

export interface OnboardingMission {
  mission_id: string;
  status: 'running' | 'done';
  client: { id: string; name: string; status: string };
  steps: OnboardingStep[];
}

export function useOnboardingMission(clientId: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['onboarding-mission', clientId],
    queryFn: () => httpClient.get<OnboardingMission>(`/api/clients/${clientId}/onboarding`),
    enabled: !!clientId,
  });
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['onboarding-mission', clientId] });
  return { ...query, invalidate };
}

export function recordBankSubmission(
  clientId: string,
  body: { custodian: string; notes?: string; document_ids?: string[] },
) {
  return httpClient.post(`/api/clients/${clientId}/onboarding/bank-submission`, body);
}

export function recordBankAccount(
  clientId: string,
  body: { account_number: string; custodian?: string },
) {
  return httpClient.post(`/api/clients/${clientId}/onboarding/bank-account`, body);
}
